import { useRef, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { keepPreviousData } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'

import { Button } from '@gto/ui/button'
import { IconButton } from '@gto/ui/icon-button'
import { ChevronLeftIcon, ChevronRightIcon } from '@gto/ui/icons/outline'
import { Skeleton } from '@gto/ui/skeleton'
import { Typography } from '@gto/ui/typography'

import type { RedemptionHistory } from '@/api/gen'
import { usePaymentRecentRedemptionsList } from '@/api/gen'
import { EdgeBalance } from '@/components/edge-balance'
import { useAuth } from '@/hooks/use-auth'

import { StatsBoxWrapper } from './stats-box-wrapper'

export const LastEdgeRedemptions = () => {
  const { isAuthorized, isInitialized } = useAuth()

  const [currentIndex, setCurrentIndex] = useState(0)
  const containerRef = useRef<HTMLDivElement>(null)

  const { data = [], isFetching } = usePaymentRecentRedemptionsList({
    query: {
      refetchOnWindowFocus: false,
      staleTime: 1000 * 60 * 5,
      gcTime: 1000 * 60 * 10,
      placeholderData: keepPreviousData,
      enabled: isAuthorized,
    },
  })

  const onNext = () => {
    setCurrentIndex((prev) => (prev + 1 >= data.length ? 0 : prev + 1))
  }
  const onPrev = () => {
    setCurrentIndex((prev) => (prev - 1 < 0 ? data.length - 1 : prev - 1))
  }

  const controls = !isFetching && isInitialized && data.length > 1 && (
    <div className="flex gap-1">
      <IconButton
        edge="start"
        onClick={onPrev}
      >
        <ChevronLeftIcon width={16} />
      </IconButton>
      <IconButton
        edge="end"
        onClick={onNext}
      >
        <ChevronRightIcon width={16} />
      </IconButton>
    </div>
  )

  return (
    <StatsBoxWrapper
      className="max-w-[33%] flex-[1_1_0px] md:max-w-full md:flex-[1_0_auto]"
      controls={controls}
      title={<FormattedMessage id="rewards.stats.lastEdgeRedemptions.title" />}
    >
      <div className="flex h-full flex-col gap-6">
        <div className="grow overflow-hidden">
          {isFetching || !isInitialized ? (
            <div className="flex items-center justify-between">
              <Skeleton
                variant="rectangular"
                width="40%"
              />
              <div className="flex flex-col items-end gap-2">
                <Skeleton
                  height={14}
                  variant="rectangular"
                  width={40}
                />
                <Skeleton
                  height={18}
                  variant="rectangular"
                  width={60}
                />
              </div>
            </div>
          ) : data.length === 0 ? (
            <Typography
              color="text.secondary"
              variant="body2"
            >
              <FormattedMessage id="rewards.stats.noRedemptions" />
            </Typography>
          ) : (
            <div
              ref={containerRef}
              className="flex w-full transition-transform duration-300 ease-in-out"
              style={{
                transform: `translateX(-${currentIndex * 100}%)`,
              }}
            >
              {data.map((redemption, index) => (
                <div
                  // eslint-disable-next-line @eslint-react/no-array-index-key -- server-ordered redemption history; slide cards are stateless display; carousel index lives on the container
                  key={index}
                  className="w-full flex-[0_0_100%] px-2"
                >
                  <LastEdgeRedemptionCard {...redemption} />
                </div>
              ))}
            </div>
          )}
        </div>

        <div>
          <Button
            viewTransition
            color="secondary"
            component={Link}
            size="small"
            to="/app/rewards/edge-history"
            variant="contained"
          >
            <FormattedMessage id="header.navigation.rewards.edgeHistory" />
          </Button>
        </div>
      </div>
    </StatsBoxWrapper>
  )
}

const LastEdgeRedemptionCard = ({ date, item_name, item_cost }: RedemptionHistory) => {
  return (
    <div className="flex items-center justify-between">
      <Typography
        className="max-w-[200px] [word-break:break-word]"
        variant="body2"
      >
        {item_name}
      </Typography>
      <div className="flex flex-col items-end">
        <Typography
          color="text.ghost"
          variant="body4"
        >
          {date}
        </Typography>
        {/* oxlint-disable-next-line typescript/no-unnecessary-type-conversion */}
        <EdgeBalance balance={Number(item_cost)} />
      </div>
    </div>
  )
}
